import React, { useEffect } from 'react';
import { setPageMeta } from '../utils/setPageMeta';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LegalDisclaimer = () => {
  useEffect(() => {
    setPageMeta(
      'Legal Disclaimer - Legal Flick',
      'Important legal disclaimer about Legal Flick\'s services. We are not a law firm and do not provide legal advice. Understand the scope of our services.'
    );
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
            Legal Disclaimer
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Not a Law Firm</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Legal Flick is not a law firm and does not provide legal advice, opinions, or recommendations. The information and services provided through our platform are for informational and document preparation purposes only.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We cannot and do not provide legal advice. If you need legal advice, you should consult with a licensed attorney in your jurisdiction.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">No Attorney-Client Relationship</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your use of Legal Flick's services does not create an attorney-client relationship between you and Legal Flick or any of our employees or contractors. Communications between you and Legal Flick may not be protected under attorney-client privilege.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Document Preparation Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Legal Flick provides self-help services at your specific direction. We prepare documents based on information you provide. We do not review your information for legal sufficiency, draw legal conclusions, or apply the law to your circumstances.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You are responsible for ensuring that the information you provide is accurate, complete, and appropriate for your specific situation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">State-Specific Requirements</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Legal requirements vary by state and jurisdiction. While we strive to provide accurate information about state-specific requirements, we cannot guarantee that our services meet all requirements of every jurisdiction.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You are responsible for verifying that the documents and services meet your state's specific requirements.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Government Filing</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When we file documents with government agencies on your behalf, we do so as your agent based on the information you provide. We cannot guarantee acceptance or approval by government agencies.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Government processing times vary and are beyond our control. Delays in processing are not the responsibility of Legal Flick.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Accuracy of Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we make reasonable efforts to ensure the accuracy of information on our website, we make no representations or warranties about the completeness, accuracy, or reliability of any information, content, or services provided.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Legal requirements and procedures change frequently. We do not guarantee that information on our site is current or reflects the most recent legal developments.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website may contain links to third-party websites. We are not responsible for the content, accuracy, or practices of third-party sites. The inclusion of any link does not imply endorsement by Legal Flick.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Professional Advice</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nothing on our website or in our services should be construed as professional legal, tax, accounting, or financial advice. You should consult with appropriate professionals regarding your specific circumstances.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Limitation on Scope of Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our services are limited to document preparation and filing. We do not:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Provide legal advice or represent you in legal matters</li>
                <li>Review your situation for legal sufficiency</li>
                <li>Recommend specific actions or decisions</li>
                <li>Represent you in court or before government agencies</li>
                <li>Provide tax, financial, or accounting advice</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Responsibility</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You acknowledge that you are solely responsible for:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Determining whether our services are appropriate for your needs</li>
                <li>Providing accurate and complete information</li>
                <li>Reviewing all documents before filing</li>
                <li>Complying with all applicable laws and regulations</li>
                <li>Seeking legal advice when necessary</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about this disclaimer, please contact us at:
              </p>
              <p className="text-gray-700 leading-relaxed">
                Email: info@legalflick.com<br />
                Address: 337 E Main St, Stockton, CA 95202, United States
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LegalDisclaimer;